import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalance,
  AccountTree,
  Apartment,
  Badge,
  BrandingWatermark,
  Business,
  Edit,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { administrationApi } from '../../api/administrationApi';
import { toast } from 'react-toastify';
import { setUser } from '../../store/slices/authSlice';

export type AccountSectionKey =
  | 'organization'
  | 'branches'
  | 'departments'
  | 'designations'
  | 'account-information'
  | 'branding';

interface AccountSettingsManagementProps {
  section: AccountSectionKey;
}

interface SectionMeta {
  title: string;
  route: string;
  icon: React.ReactNode;
}

interface OrganizationItem {
  id: string;
  organizationFullName: string;
  organizationShortName: string;
  isActive: boolean;
}

interface BranchItem {
  id: string;
  organizationId?: string | null;
  branchName: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  phone?: string;
  isActive: boolean;
}

interface DepartmentItem {
  id: string;
  branchId?: string | null;
  departmentName: string;
  description?: string;
  isActive: boolean;
}

interface DesignationItem {
  id: string;
  departmentId?: string | null;
  designationName: string;
  description?: string;
  isActive: boolean;
}

interface AccountInformationItem {
  id: string;
  companyName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  zipPostalCode?: string;
  country?: string;
  state?: string;
  gstin: string;
  isActive: boolean;
}

interface AccountFormState {
  organizationFullName: string;
  organizationShortName: string;
  organizationId: string;
  branchName: string;
  address: string;
  city: string;
  state: string;
  country: string;
  phone: string;
  branchId: string;
  departmentName: string;
  departmentDescription: string;
  departmentId: string;
  designationName: string;
  designationDescription: string;
  companyName: string;
  addressLine1: string;
  addressLine2: string;
  accountCity: string;
  zipPostalCode: string;
  accountCountry: string;
  accountState: string;
  gstin: string;
  isActive: boolean;
}

interface DisplayRow {
  id: string;
  name: string;
  description: string;
  status: string;
}

const sectionMetaMap: Record<AccountSectionKey, SectionMeta> = {
  organization: {
    title: 'Organization',
    route: '/settings/account-management/organization',
    icon: <Business fontSize="small" />,
  },
  branches: {
    title: 'Branches',
    route: '/settings/account-management/branches',
    icon: <Apartment fontSize="small" />,
  },
  departments: {
    title: 'Departments',
    route: '/settings/account-management/departments',
    icon: <AccountTree fontSize="small" />,
  },
  designations: {
    title: 'Designations',
    route: '/settings/account-management/designations',
    icon: <Badge fontSize="small" />,
  },
  'account-information': {
    title: 'Account Information',
    route: '/settings/account-management/account-information',
    icon: <AccountBalance fontSize="small" />,
  },
  branding: {
    title: 'Branding',
    route: '/settings/account-management/branding',
    icon: <BrandingWatermark fontSize="small" />,
  },
};

const sectionOrder: AccountSectionKey[] = [
  'organization',
  'branches',
  'departments',
  'designations',
  'account-information',
  'branding',
];

const hiddenSections: AccountSectionKey[] = ['account-information', 'branding'];
const visibleSectionOrder = sectionOrder.filter((key) => !hiddenSections.includes(key));

const emptyForm: AccountFormState = {
  organizationFullName: '',
  organizationShortName: '',
  organizationId: '',
  branchName: '',
  address: '',
  city: '',
  state: '',
  country: '',
  phone: '',
  branchId: '',
  departmentName: '',
  departmentDescription: '',
  departmentId: '',
  designationName: '',
  designationDescription: '',
  companyName: '',
  addressLine1: '',
  addressLine2: '',
  accountCity: '',
  zipPostalCode: '',
  accountCountry: '',
  accountState: '',
  gstin: '',
  isActive: true,
};

const cardSx = {
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
};

const gstinRegex = /^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/;
const unsafeMarkupPattern = /[<>]|&(?:lt|gt|#0*60|#0*62|#x0*3c|#x0*3e);?/i;

const normalizeDuplicateValue = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();
const containsUnsafeMarkup = (value: string) => unsafeMarkupPattern.test(value);

const trimFormValues = (input: AccountFormState): AccountFormState => {
  const next = { ...input };
  const mutableNext = next as Record<keyof AccountFormState, string | boolean>;

  (Object.keys(next) as (keyof AccountFormState)[]).forEach((key) => {
    const value = next[key];
    if (typeof value === 'string') {
      mutableNext[key] = value.trim();
    }
  });

  return next;
};

const AccountSettingsManagement: React.FC<AccountSettingsManagementProps> = ({ section }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<AccountFormState>(emptyForm);

  const [organizations, setOrganizations] = useState<OrganizationItem[]>([]);
  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [departments, setDepartments] = useState<DepartmentItem[]>([]);
  const [designations, setDesignations] = useState<DesignationItem[]>([]);
  const [accountInfos, setAccountInfos] = useState<AccountInformationItem[]>([]);

  const currentIndex = useMemo(() => visibleSectionOrder.indexOf(section), [section]);
  const currentMeta = sectionMetaMap[section];

  const updateForm = (key: keyof AccountFormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const organizationNameById = useMemo(
    () => new Map(organizations.map((item) => [item.id, item.organizationFullName])),
    [organizations],
  );

  const branchNameById = useMemo(
    () => new Map(branches.map((item) => [item.id, item.branchName])),
    [branches],
  );

  const departmentNameById = useMemo(
    () => new Map(departments.map((item) => [item.id, item.departmentName])),
    [departments],
  );

const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
  const value = e.target.value;
  const regex = /^[A-Za-z ]*$/;

  if (regex.test(value)) {
    updateForm('designationName', value);
  }
};

  const loadCurrentSection = useCallback(async () => {
    if (!user?.tenantId) return;

    setLoading(true);
    setError(null);

    try {
      if (section === 'organization') {
        setOrganizations(await administrationApi.getOrganizations(user.tenantId));
      }

      if (section === 'branches') {
        const [organizationList, branchList] = await Promise.all([
          administrationApi.getOrganizations(user.tenantId),
          administrationApi.getBranches(user.tenantId),
        ]);
        setOrganizations(organizationList);
        setBranches(branchList);
      }

      if (section === 'departments') {
        const [branchList, departmentList] = await Promise.all([
          administrationApi.getBranches(user.tenantId),
          administrationApi.getDepartments(user.tenantId),
        ]);
        setBranches(branchList);
        setDepartments(departmentList);
      }

      if (section === 'designations') {
        const [departmentList, designationList] = await Promise.all([
          administrationApi.getDepartments(user.tenantId),
          administrationApi.getDesignations(user.tenantId),
        ]);
        setDepartments(departmentList);
        setDesignations(designationList);
      }

      if (section === 'account-information') {
        setAccountInfos(await administrationApi.getAccountInformation(user.tenantId));
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load records.');
    } finally {
      setLoading(false);
    }
  }, [section, user?.tenantId]);

  React.useEffect(() => {
    loadCurrentSection();
  }, [loadCurrentSection]);

  const tableRows = useMemo<DisplayRow[]>(() => {
    if (section === 'organization') {
      return organizations.map((x) => ({
        id: x.id,
        name: x.organizationFullName,
        description: x.organizationShortName,
        status: x.isActive ? 'Active' : 'Inactive',
      }));
    }

    if (section === 'branches') {
      return branches.map((x) => ({
        id: x.id,
        name: x.branchName,
        description: x.organizationId ? organizationNameById.get(x.organizationId) || '-' : '-',
        status: x.isActive ? 'Active' : 'Inactive',
      }));
    }

    if (section === 'departments') {
      return departments.map((x) => ({
        id: x.id,
        name: x.departmentName,
        description: x.branchId ? branchNameById.get(x.branchId) || '-' : '-',
        status: x.isActive ? 'Active' : 'Inactive',
      }));
    }

    if (section === 'designations') {
      return designations.map((x) => ({
        id: x.id,
        name: x.designationName,
        description: x.departmentId ? departmentNameById.get(x.departmentId) || '-' : '-',
        status: x.isActive ? 'Active' : 'Inactive',
      }));
    }

    if (section === 'account-information') {
      return accountInfos.map((x) => ({
        id: x.id,
        name: x.companyName,
        description: `${x.city} | GSTIN: ${x.gstin}`,
        status: x.isActive ? 'Active' : 'Inactive',
      }));
    }

    return [];
  }, [section, organizations, branches, departments, designations, accountInfos, organizationNameById, branchNameById, departmentNameById]);

  const handleTabChange = (_event: React.SyntheticEvent, value: number) => {
    const selectedSection = visibleSectionOrder[value];
    navigate(sectionMetaMap[selectedSection].route);
  };

  const openAddDialog = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
    setError(null);
  };

  const openEditDialog = (id: string) => {
    setEditingId(id);
    setError(null);

    if (section === 'organization') {
      const item = organizations.find((x) => x.id === id);
      if (item) {
        setForm({
          ...emptyForm,
          organizationFullName: item.organizationFullName,
          organizationShortName: item.organizationShortName,
          isActive: item.isActive,
        });
      }
    }

    if (section === 'branches') {
      const item = branches.find((x) => x.id === id);
      if (item) {
        setForm({
          ...emptyForm,
          organizationId: item.organizationId || '',
          branchName: item.branchName,
          address: item.address || '',
          city: item.city || '',
          state: item.state || '',
          country: item.country || '',
          phone: item.phone || '',
          isActive: item.isActive,
        });
      }
    }

    if (section === 'departments') {
      const item = departments.find((x) => x.id === id);
      if (item) {
        setForm({
          ...emptyForm,
          branchId: item.branchId || '',
          departmentName: item.departmentName,
          departmentDescription: item.description || '',
          isActive: item.isActive,
        });
      }
    }

    if (section === 'designations') {
      const item = designations.find((x) => x.id === id);
      if (item) {
        setForm({
          ...emptyForm,
          departmentId: item.departmentId || '',
          designationName: item.designationName,
          designationDescription: item.description || '',
          isActive: item.isActive,
        });
      }
    }

    if (section === 'account-information') {
      const item = accountInfos.find((x) => x.id === id);
      if (item) {
        setForm({
          ...emptyForm,
          companyName: item.companyName,
          addressLine1: item.addressLine1,
          addressLine2: item.addressLine2 || '',
          accountCity: item.city,
          zipPostalCode: item.zipPostalCode || '',
          accountCountry: item.country || '',
          accountState: item.state || '',
          gstin: item.gstin,
          isActive: item.isActive,
        });
      }
    }

    setDialogOpen(true);
  };

  const validateForm = (source: AccountFormState = form): boolean => {
    const requiredFieldsBySection: Record<AccountSectionKey, { key: keyof AccountFormState; label: string }[]> = {
      organization: [
        { key: 'organizationFullName', label: 'Organization Name' },
        { key: 'organizationShortName', label: 'Organization Code' },
      ],
      branches: [
        { key: 'organizationId', label: 'Organization' },
        { key: 'branchName', label: 'Branch Name' },
        { key: 'address', label: 'Address' },
        { key: 'city', label: 'City' },
        { key: 'state', label: 'State' },
        { key: 'country', label: 'Country' },
        { key: 'phone', label: 'Phone' },
      ],
      departments: [
        { key: 'branchId', label: 'Branch' },
        { key: 'departmentName', label: 'Department Name' },
        { key: 'departmentDescription', label: 'Description' },
      ],
      designations: [
        { key: 'departmentId', label: 'Department' },
        { key: 'designationName', label: 'Designation Name' },
        { key: 'designationDescription', label: 'Description' },
      ],
      'account-information': [
        { key: 'companyName', label: 'Company Name' },
        { key: 'addressLine1', label: 'Address Line 1' },
        { key: 'accountCity', label: 'City' },
        { key: 'accountState', label: 'State' },
        { key: 'accountCountry', label: 'Country' },
        { key: 'zipPostalCode', label: 'Zip / Postal Code' },
        { key: 'gstin', label: 'GSTIN' },
      ],
      branding: [],
    };

    const missingField = requiredFieldsBySection[section].find(({ key }) => {
      const value = source[key];
      return typeof value === 'string' ? value.trim() === '' : false;
    });

    if (missingField) {
      toast.error(`${missingField.label} is required.`);
      return false;
    }

    if (section === 'branches') {
      if (containsUnsafeMarkup(source.branchName)) {
        const message = 'Branch Name cannot contain HTML or script content.';
        setError(message);
        toast.error(message, { position: 'top-center' });
        return false;
      }

      const phoneDigits = source.phone.trim();
      if (!/^\d{10}$/.test(phoneDigits)) {
        toast.error('Phone number must be exactly 10 digits.');
        return false;
      }
    }

    if (section === 'organization') {
      const organizationName = source.organizationFullName.trim();
      if (!/^[A-Za-z ]+$/.test(organizationName)) {
        toast.error('Organization Name can contain only letters and spaces.');
        return false;
      }
    }

    if (section === 'account-information') {
      if (!/^\d+$/.test(source.zipPostalCode.trim())) {
        toast.error('Zip / Postal Code must contain numbers only.');
        return false;
      }

      if (!gstinRegex.test(source.gstin.trim().toUpperCase())) {
        toast.error('Please enter a valid GSTIN format (e.g., 22ABCDE1234F1Z5).');
        return false;
      }
    }

    const hasDuplicateValue = <T extends { id: string }>(
      items: T[],
      getValue: (item: T) => string,
      candidateValue: string,
    ) => {
      const normalizedCandidate = normalizeDuplicateValue(candidateValue);

      return items.some((item) => {
        if (editingId && item.id === editingId) {
          return false;
        }

        return normalizeDuplicateValue(getValue(item)) === normalizedCandidate;
      });
    };

    if (
      section === 'organization' &&
      hasDuplicateValue(organizations, (item) => item.organizationFullName, source.organizationFullName)
    ) {
      const message = 'Organization name already exists for this tenant.';
      setError(message);
      toast.error(message);
      return false;
    }

    if (
      section === 'branches' &&
      branches.some((item) =>
        (!editingId || item.id !== editingId) &&
        item.organizationId === source.organizationId &&
        normalizeDuplicateValue(item.branchName) === normalizeDuplicateValue(source.branchName)
      )
    ) {
      const message = 'Branch name already exists for this tenant.';
      setError(message);
      toast.error(message);
      return false;
    }

    if (
      section === 'departments' &&
      departments.some((item) =>
        (!editingId || item.id !== editingId) &&
        item.branchId === source.branchId &&
        normalizeDuplicateValue(item.departmentName) === normalizeDuplicateValue(source.departmentName)
      )
    ) {
      const message = 'Department name already exists for this tenant.';
      setError(message);
      toast.error(message);
      return false;
    }

    if (
      section === 'designations' &&
      designations.some((item) =>
        (!editingId || item.id !== editingId) &&
        item.departmentId === source.departmentId &&
        normalizeDuplicateValue(item.designationName) === normalizeDuplicateValue(source.designationName)
      )
    ) {
      const message = 'Designation name already exists for this tenant.';
      setError(message);
      toast.error(message);
      return false;
    }

    if (
      section === 'account-information' &&
      hasDuplicateValue(accountInfos, (item) => item.companyName, source.companyName)
    ) {
      const message = 'Company name already exists for this tenant.';
      setError(message);
      toast.error(message);
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!user?.tenantId) {
      setError('Tenant not found for current user. Please login again.');
      return;
    }

    if (section === 'branding') {
      setError('Branding endpoint is not available in backend yet.');
      return;
    }

    const trimmedForm = trimFormValues(form);
    setForm(trimmedForm);

    if (!validateForm(trimmedForm)) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (section === 'organization') {
        if (editingId) {
          await administrationApi.updateOrganization({
            id: editingId,
            organizationFullName: trimmedForm.organizationFullName,
            organizationShortName: trimmedForm.organizationShortName,
            isActive: trimmedForm.isActive,
            modifiedBy: user.fullName || 'system',
          });
        } else {
          await administrationApi.createOrganization({
            organizationFullName: trimmedForm.organizationFullName,
            organizationShortName: trimmedForm.organizationShortName,
            isActive: trimmedForm.isActive,
            tenantId: user.tenantId,
            createdBy: user.fullName || 'system',
          });
        }
      }

      if (section === 'branches') {
        if (editingId) {
          await administrationApi.updateBranch({
            id: editingId,
            organizationId: trimmedForm.organizationId,
            branchName: trimmedForm.branchName,
            address: trimmedForm.address,
            city: trimmedForm.city,
            state: trimmedForm.state,
            country: trimmedForm.country,
            phone: trimmedForm.phone,
            isActive: trimmedForm.isActive,
            modifiedBy: user.fullName || 'system',
          });
        } else {
          await administrationApi.createBranch({
            organizationId: trimmedForm.organizationId,
            branchName: trimmedForm.branchName,
            address: trimmedForm.address,
            city: trimmedForm.city,
            state: trimmedForm.state,
            country: trimmedForm.country,
            phone: trimmedForm.phone,
            isActive: trimmedForm.isActive,
            tenantId: user.tenantId,
            createdBy: user.fullName || 'system',
          });
        }
      }

      if (section === 'departments') {
        if (editingId) {
          await administrationApi.updateDepartment({
            id: editingId,
            branchId: trimmedForm.branchId,
            departmentName: trimmedForm.departmentName,
            description: trimmedForm.departmentDescription,
            isActive: trimmedForm.isActive,
            modifiedBy: user.fullName || 'system',
          });
        } else {
          await administrationApi.createDepartment({
            branchId: trimmedForm.branchId,
            departmentName: trimmedForm.departmentName,
            description: trimmedForm.departmentDescription,
            isActive: trimmedForm.isActive,
            tenantId: user.tenantId,
            createdBy: user.fullName || 'system',
          });
        }
      }

      if (section === 'designations') {
        if (editingId) {
          await administrationApi.updateDesignation({
            id: editingId,
            departmentId: trimmedForm.departmentId,
            designationName: trimmedForm.designationName,
            description: trimmedForm.designationDescription,
            isActive: trimmedForm.isActive,
            modifiedBy: user.fullName || 'system',
          });
        } else {
          await administrationApi.createDesignation({
            departmentId: trimmedForm.departmentId,
            designationName: trimmedForm.designationName,
            description: trimmedForm.designationDescription,
            isActive: trimmedForm.isActive,
            tenantId: user.tenantId,
            createdBy: user.fullName || 'system',
          });
        }
      }

      if (section === 'account-information') {
        if (editingId) {
          await administrationApi.updateAccountInformation({
            id: editingId,
            companyName: trimmedForm.companyName,
            addressLine1: trimmedForm.addressLine1,
            addressLine2: trimmedForm.addressLine2,
            city: trimmedForm.accountCity,
            zipPostalCode: trimmedForm.zipPostalCode,
            country: trimmedForm.accountCountry,
            state: trimmedForm.accountState,
            gstin: trimmedForm.gstin,
            isActive: trimmedForm.isActive,
            modifiedBy: user.userId,
          });
        } else {
          await administrationApi.createAccountInformation({
            companyName: trimmedForm.companyName,
            addressLine1: trimmedForm.addressLine1,
            addressLine2: trimmedForm.addressLine2,
            city: trimmedForm.accountCity,
            zipPostalCode: trimmedForm.zipPostalCode,
            country: trimmedForm.accountCountry,
            state: trimmedForm.accountState,
            gstin: trimmedForm.gstin,
            isActive: trimmedForm.isActive,
            tenantId: user.tenantId,
            createdBy: user.userId,
          });
        }
      }

      setDialogOpen(false);
      await loadCurrentSection();
      if (section === 'account-information' && trimmedForm.companyName && trimmedForm.companyName !== user?.companyName) {
        const update: { companyName: string; fullName?: string } = {
          companyName: trimmedForm.companyName,
        };

        if (user?.companyName && user.fullName?.includes(user.companyName)) {
          update.fullName = user.fullName.replace(user.companyName, trimmedForm.companyName);
        }

        dispatch(setUser(update));
      }
      toast.success(`${currentMeta.title} saved successfully.`, { position: 'top-center' });
    } catch (e: any) {
      const message = e?.response?.data?.message || 'Failed to save settings.';
      setError(message);

      if (section === 'branches' || section === 'departments' || section === 'designations') {
        toast.error(message, { position: 'top-center' });
      } else {
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const renderDialogFields = () => {
    if (section === 'organization') {
      return (
        <Stack spacing={2} mt={1}>
          <TextField
            fullWidth
            required
            label="Organization Name"
            value={form.organizationFullName}
            onChange={(e) => {
              const value = e.target.value;

                        // allow only letters and space & limit 100 chars
                        if (/^[a-zA-Z ]{0,100}$/.test(value)) {
                updateForm('organizationFullName', value);
              }
            }}
            error={form.organizationFullName.length > 100}
            helperText={
              form.organizationFullName.length > 100
                          ? "Maximum 100 characters allowed"
                : `${form.organizationFullName.length}/100`
            }
          />

          <TextField
            fullWidth
            required
            label="Organization Code"
            value={form.organizationShortName}
            onChange={(e) => {
              const value = e.target.value;

              // allow only letters, numbers, space & limit 20 chars
              if (/^[a-zA-Z0-9 -]{0,20}$/.test(value)) {
                updateForm('organizationShortName', value);
              }
            }}
            error={form.organizationShortName.length > 20}
            helperText={
              form.organizationShortName.length > 20
                ? "Maximum 20 characters allowed (no special characters)"
                : `${form.organizationShortName.length}/20`
            }
          />
          <FormControlLabel
            control={<Switch checked={form.isActive} onChange={(e) => updateForm('isActive', e.target.checked)} />}
            label="Active"
          />
        </Stack>
      );
    }

    if (section === 'branches') {
      return (
        <Stack spacing={2} mt={1}>
          <TextField
            fullWidth
            required
            select
            label="Organization"
            value={form.organizationId}
            onChange={(e) => updateForm('organizationId', e.target.value)}
            helperText={organizations.length === 0 ? 'Create an organization before adding branches.' : ''}
          >
            {organizations.map((organization) => (
              <MenuItem key={organization.id} value={organization.id}>
                {organization.organizationFullName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            required
            label="Branch Name"
            value={form.branchName}
            onChange={(e) => updateForm('branchName', e.target.value)}
            inputProps={{ maxLength: 100 }}
            error={containsUnsafeMarkup(form.branchName)}
            helperText={
              containsUnsafeMarkup(form.branchName)
                ? 'Branch Name cannot contain HTML or script content.'
                : `${form.branchName.length}/100 characters`
            }
          />
          <TextField fullWidth required label="Address" value={form.address} onChange={(e) => updateForm('address', e.target.value)} inputProps={{ maxLength: 200 }} helperText={`${form.address.length}/200 characters`} />
          <TextField fullWidth required label="City" value={form.city} onChange={(e) => updateForm('city', e.target.value.replace(/[^a-zA-Z ]/g, ''))} inputProps={{ maxLength: 100 }} helperText={`${form.city.length}/100 characters`} />
          <TextField fullWidth required label="State" value={form.state} onChange={(e) => updateForm('state', e.target.value.replace(/[^a-zA-Z ]/g, ''))} inputProps={{ maxLength: 100 }} helperText={`${form.state.length}/100 characters`} />
          <TextField fullWidth required label="Country" value={form.country} onChange={(e) => updateForm('country', e.target.value.replace(/[^a-zA-Z ]/g, ''))} inputProps={{ maxLength: 100 }} helperText={`${form.country.length}/100 characters`} />
          <TextField fullWidth required label="Phone" 
          value={form.phone} 
          onChange={(e) => updateForm('phone',e.target.value.replace(/\D/g, ''))} 
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 10 }} helperText={`${form.phone.length}/10 characters(numeric only)`}  />
          <FormControlLabel
            control={<Switch checked={form.isActive} onChange={(e) => updateForm('isActive', e.target.checked)} />}
            label="Active"
          />
        </Stack>
      );
    }

    if (section === 'departments') {
      return (
        <Stack spacing={2} mt={1}>
          <TextField
            fullWidth
            required
            select
            label="Branch"
            value={form.branchId}
            onChange={(e) => updateForm('branchId', e.target.value)}
            helperText={branches.length === 0 ? 'Create a branch before adding departments.' : ''}
          >
            {branches.map((branch) => (
              <MenuItem key={branch.id} value={branch.id}>
                {branch.branchName}
              </MenuItem>
            ))}
          </TextField>
          <TextField fullWidth required label="Department Name" value={form.departmentName} onChange={(e) => updateForm('departmentName', e.target.value)} inputProps={{ maxLength: 100 }} helperText={`${form.departmentName.length}/100 characters`} />
          <TextField fullWidth required label="Description" value={form.departmentDescription} onChange={(e) => updateForm('departmentDescription', e.target.value)} inputProps={{ maxLength: 200 }} helperText={`${form.departmentDescription.length}/200 characters`} />
          <FormControlLabel
            control={<Switch checked={form.isActive} onChange={(e) => updateForm('isActive', e.target.checked)} />}
            label="Active"
          />
        </Stack>
      );
    }

    if (section === 'designations') {
      return (
        <Stack spacing={2} mt={1}>
        <TextField
            fullWidth
            required
            select
            label="Department"
            value={form.departmentId}
            onChange={(e) => updateForm('departmentId', e.target.value)}
            helperText={departments.length === 0 ? 'Create a department before adding designations.' : ''}
          >
            {departments.map((department) => (
              <MenuItem key={department.id} value={department.id}>
                {department.departmentName}
              </MenuItem>
            ))}
          </TextField>
        <TextField
            fullWidth
            required
            label="Designation Name"
            value={form.designationName}
            onChange={handleNameChange}
            inputProps={{ maxLength: 50 }}
          />
          <TextField fullWidth required label="Description" value={form.designationDescription} onChange={(e) => updateForm('designationDescription', e.target.value)} inputProps={{ maxLength: 200 }} helperText={`${form.designationDescription.length}/200 characters`} />
          <FormControlLabel
            control={<Switch checked={form.isActive} onChange={(e) => updateForm('isActive', e.target.checked)} />}
            label="Active"
          />
        </Stack>
      );
    }

    return (
      <Stack spacing={2} mt={1}>
        <TextField
          fullWidth
          required
          label="Company Name"
          value={form.companyName}
          onChange={(e) => {
            const value = e.target.value;
            if (value.length <= 100) {
              updateForm("companyName", value);
            }
          }}
          inputProps={{ maxLength: 100 }}
          helperText={`${form.companyName.length}/100 characters`}
          error={form.companyName.length === 100}
        />
        <TextField fullWidth required label="Address Line 1" value={form.addressLine1} onChange={(e) => updateForm('addressLine1', e.target.value)} inputProps={{ maxLength: 200 }} helperText={`${form.addressLine1.length}/200 characters`} />
        <TextField fullWidth label="Address Line 2" value={form.addressLine2} onChange={(e) => updateForm('addressLine2', e.target.value)} inputProps={{ maxLength: 200 }} helperText={`${form.addressLine2.length}/200 characters`} />
        <TextField fullWidth required label="City" value={form.accountCity} onChange={(e) => updateForm('accountCity', e.target.value)} inputProps={{ maxLength: 100 }} helperText={`${form.accountCity.length}/100 characters`} />
        <TextField fullWidth required label="State" value={form.accountState} onChange={(e) => updateForm('accountState', e.target.value)} inputProps={{ maxLength: 100 }} helperText={`${form.accountState.length}/100 characters`} />
        <TextField fullWidth required label="Country" value={form.accountCountry} onChange={(e) => updateForm('accountCountry', e.target.value)} inputProps={{ maxLength: 100 }} helperText={`${form.accountCountry.length}/100 characters`} />
        <TextField
          fullWidth
          required
          label="Zip / Postal Code"
          value={form.zipPostalCode}
          onChange={(e) => updateForm('zipPostalCode', e.target.value.replace(/\D/g, ''))}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }} 
          helperText={`${form.zipPostalCode.length}/6 characters (numbers only)`} 
        />
        <TextField
          fullWidth
          required
          label="GSTIN"
          value={form.gstin}
          onChange={(e) => updateForm('gstin', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15))}
          inputProps={{ maxLength: 15 }}
          helperText={`${form.gstin.length}/15 characters`}
        />
        <FormControlLabel
          control={<Switch checked={form.isActive} onChange={(e) => updateForm('isActive', e.target.checked)} />}
          label="Active"
        />
      </Stack>
    );
  };

  const secondaryColumnLabel =
    section === 'branches'
      ? 'Organization'
      : section === 'departments'
        ? 'Branch'
        : section === 'designations'
          ? 'Department'
          : 'Description';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Card sx={{ ...cardSx, overflow: 'hidden' }}>
        <Tabs
          value={currentIndex < 0 ? 0 : currentIndex}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1.02rem',
              minHeight: 58,
            },
          }}
        >
          {visibleSectionOrder.map((key) => (
            <Tab key={key} label={sectionMetaMap[key].title} />
          ))}
        </Tabs>
      </Card>

      <Card sx={cardSx}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              {currentMeta.icon}
              <Typography variant="h5">{currentMeta.title}</Typography>
            </Stack>
            <Button
              variant="contained"
              sx={{ textTransform: 'none', borderRadius: 2, minWidth: 110 }}
              onClick={openAddDialog}
              disabled={section === 'branding'}
            >
              + Add
            </Button>
          </Stack>

          {loading ? (
            <Stack direction="row" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Stack>
          ) : section === 'branding' ? (
            <Alert severity="info">Branding is not implemented yet.</Alert>
          ) : tableRows.length === 0 ? (
            <Alert severity="info">No records found for this tenant.</Alert>
          ) : (
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: '32%' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '42%' }}>{secondaryColumnLabel}</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '14%' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '12%' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.description || '-'}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Edit">
                            <IconButton color="primary" onClick={() => openEditDialog(row.id)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {/* <Tooltip title="Delete endpoint not available">
                            <span>
                              <IconButton color="error" disabled>
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip> */}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {error && section !== 'branches' && section !== 'departments' && section !== 'designations' && (
        <Alert severity="error">{error}</Alert>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? `Edit ${currentMeta.title}` : `Add ${currentMeta.title}`}</DialogTitle>
        <DialogContent>{renderDialogFields()}</DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDialogOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              saving ||
              (section === 'branches' && !form.organizationId) ||
              (section === 'departments' && !form.branchId) ||
              (section === 'designations' && (!form.departmentId || form.designationName.trim().length < 3))
            }
            sx={{ textTransform: 'none' }}
          >
            {editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountSettingsManagement;
